import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import {
  HeartPulse, Save, Trash2, ShieldCheck, Info, ChevronDown, ChevronUp,
  QrCode, Printer, CalendarIcon,
} from "lucide-react";
import { getMedicalProfile, saveMedicalProfile, clearMedicalProfile } from "../lib/api";
import { userIdentity } from "../lib/identity";
import { API } from "../lib/api";
import { EmergencyContactsSection } from "./ContactsPage";
import { useCountryConfig } from "../lib/useCountryConfig";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";

// ─── Whitelisted medical conditions ────────────────────────────────
// The order below is the exact order rendered as checkboxes on the
// profile form. `other_condition` (free-text) is a separate field, so
// "other" is NOT in this list.
const CONDITIONS = [
  { key: "diabetes",       label: "Diabète" },
  { key: "hypertension",   label: "Hypertension artérielle" },
  { key: "heart",          label: "Problèmes cardiaques" },
  { key: "asthma",         label: "Asthme" },
  { key: "seizures",       label: "Épilepsie / crises" },
  { key: "stroke_history", label: "Antécédent d'AVC" },
  { key: "sickle_cell",    label: "Drépanocytose" },
  { key: "severe_allergy", label: "Allergie sévère" },
  { key: "pregnancy",      label: "Grossesse" },
  { key: "disability",     label: "Handicap / mobilité réduite" },
];

const BLOOD_TYPES = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

const formatDobDisplay = (d) => {
  if (!d) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
};

const EMPTY = {
  full_name: "", date_of_birth: "", age: "", blood_type: "",
  conditions: [], other_condition: "",
  allergies: "", medications: "", disability: "",
  pregnancy: false, preferred_language: "", notes: "",
};

// ─── Emergency medical profile form ────────────────────────────────
// Renders as a single collapsible card. Every field is optional. The
// citizen may save, clear, or ignore the section entirely.
function MedicalProfileSection() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Sep 2026 bugfix — see identity.js `ready()` comment.
        await userIdentity.ready();
        const data = await getMedicalProfile();
        if (!cancelled && data) {
          setForm({
            ...EMPTY,
            ...data,
            // date_of_birth stored as ISO string; input[type=date] wants YYYY-MM-DD.
            date_of_birth: (data.date_of_birth || "").slice(0, 10),
            age: data.age ?? "",
            conditions: Array.isArray(data.conditions) ? data.conditions : [],
            pregnancy: !!data.pregnancy,
          });
        }
      } catch (_e) {
        if (!cancelled) toast.error("Erreur de chargement du profil");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleCondition = (key) => {
    setForm((f) => {
      const set = new Set(f.conditions);
      if (set.has(key)) set.delete(key); else set.add(key);
      return { ...f, conditions: [...set] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        age: form.age === "" ? null : Number(form.age),
      };
      await saveMedicalProfile(payload);
      toast.success("Profil médical enregistré");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Enregistrement impossible");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Effacer votre profil médical ?")) return;
    try {
      await clearMedicalProfile();
      setForm(EMPTY);
      toast.success("Profil médical effacé");
    } catch (_e) {
      toast.error("Impossible d'effacer le profil");
    }
  };

  const dobDate = form.date_of_birth ? new Date(`${form.date_of_birth}T00:00:00`) : null;

  return (
    <section
      className="mx-5 mb-6 border-2 border-rescue-line bg-white"
      data-testid="medical-profile-section"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3.5 border-b-2 border-rescue-line bg-mali-navy text-white"
        data-testid="medical-profile-toggle"
      >
        <div className="flex items-center gap-2">
          <HeartPulse className="w-4 h-4" />
          <span className="text-[11px] font-bold uppercase tracking-[0.2em]">Profil médical d’urgence</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <form onSubmit={submit} className="p-4 space-y-4">
          <p className="text-[11px] leading-relaxed text-rescue-mute flex gap-2" data-testid="medical-profile-privacy-optional">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-mali-yellow" />
            <span>Ces informations sont facultatives. Elles peuvent aider les secours en cas d’urgence.</span>
          </p>

          {loading ? (
            <div className="text-sm text-rescue-mute text-center py-6">Chargement…</div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-rescue-mute">
                  Nom complet
                  <input
                    data-testid="medical-full-name"
                    value={form.full_name || ""}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    className="mt-1 w-full border-2 border-rescue-line bg-rescue-bg focus:bg-white focus:border-rescue-ink focus:outline-none px-3 py-2 text-sm font-normal normal-case tracking-normal text-rescue-ink"
                  />
                </label>
                <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-rescue-mute">
                  Date de naissance
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="medical-dob"
                        className="mt-1 w-full flex items-center justify-between gap-2 border-2 border-rescue-line bg-rescue-bg hover:bg-white focus:bg-white focus:border-rescue-ink focus:outline-none px-3 py-2 text-sm font-normal normal-case tracking-normal text-left text-rescue-ink"
                      >
                        <span className={form.date_of_birth ? "" : "text-rescue-mute"}>
                          {form.date_of_birth ? formatDobDisplay(dobDate) : "JJ/MM/AAAA"}
                        </span>
                        <CalendarIcon className="w-4 h-4 text-rescue-mute shrink-0" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start" data-testid="medical-dob-calendar">
                      <Calendar
                        mode="single"
                        selected={dobDate}
                        captionLayout="dropdown"
                        fromYear={1920}
                        toYear={new Date().getFullYear()}
                        defaultMonth={dobDate || new Date(2000, 0, 1)}
                        disabled={{ after: new Date() }}
                        onSelect={(d) => {
                          if (!d) return;
                          const yyyy = d.getFullYear();
                          const mm = String(d.getMonth() + 1).padStart(2, "0");
                          const dd = String(d.getDate()).padStart(2, "0");
                          setForm({ ...form, date_of_birth: `${yyyy}-${mm}-${dd}` });
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </label>
                <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-rescue-mute">
                  Âge
                  <input
                    type="number" min="0" max="130" inputMode="numeric"
                    data-testid="medical-age"
                    value={form.age ?? ""}
                    onChange={(e) => setForm({ ...form, age: e.target.value })}
                    className="mt-1 w-full border-2 border-rescue-line bg-rescue-bg focus:bg-white focus:border-rescue-ink focus:outline-none px-3 py-2 text-sm font-normal normal-case tracking-normal text-rescue-ink"
                  />
                </label>
                <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-rescue-mute">
                  Groupe sanguin
                  <select
                    data-testid="medical-blood-type"
                    value={form.blood_type || ""}
                    onChange={(e) => setForm({ ...form, blood_type: e.target.value })}
                    className="mt-1 w-full border-2 border-rescue-line bg-rescue-bg focus:bg-white focus:border-rescue-ink focus:outline-none px-3 py-2 text-sm font-normal normal-case tracking-normal text-rescue-ink"
                  >
                    <option value="">— Non renseigné —</option>
                    {BLOOD_TYPES.map((bt) => <option key={bt} value={bt}>{bt}</option>)}
                  </select>
                </label>
              </div>

              <fieldset className="space-y-2">
                <legend className="text-[10px] font-bold uppercase tracking-[0.15em] text-rescue-mute">Conditions médicales</legend>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid="medical-conditions-grid">
                  {CONDITIONS.map((c) => (
                    <label key={c.key} className="flex items-center gap-2 border-2 border-rescue-line px-3 py-2 text-sm cursor-pointer hover:bg-mali-yellow/5">
                      <input
                        type="checkbox"
                        data-testid={`medical-condition-${c.key}`}
                        checked={form.conditions.includes(c.key)}
                        onChange={() => toggleCondition(c.key)}
                        className="w-4 h-4"
                      />
                      <span>{c.label}</span>
                    </label>
                  ))}
                </div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-rescue-mute">
                  Autre condition
                  <input
                    data-testid="medical-other-condition"
                    value={form.other_condition || ""}
                    onChange={(e) => setForm({ ...form, other_condition: e.target.value })}
                    placeholder="Ex : trouble neurologique, cancer en traitement…"
                    className="mt-1 w-full border-2 border-rescue-line bg-rescue-bg focus:bg-white focus:border-rescue-ink focus:outline-none px-3 py-2 text-sm font-normal normal-case tracking-normal text-rescue-ink"
                  />
                </label>
              </fieldset>

              <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-rescue-mute">
                Allergies
                <textarea
                  data-testid="medical-allergies"
                  value={form.allergies || ""}
                  onChange={(e) => setForm({ ...form, allergies: e.target.value })}
                  rows={2}
                  placeholder="Ex : pénicilline, arachides…"
                  className="mt-1 w-full border-2 border-rescue-line bg-rescue-bg focus:bg-white focus:border-rescue-ink focus:outline-none px-3 py-2 text-sm font-normal normal-case tracking-normal text-rescue-ink"
                />
              </label>
              <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-rescue-mute">
                Traitements en cours
                <textarea
                  data-testid="medical-medications"
                  value={form.medications || ""}
                  onChange={(e) => setForm({ ...form, medications: e.target.value })}
                  rows={2}
                  placeholder="Ex : insuline, anticoagulant…"
                  className="mt-1 w-full border-2 border-rescue-line bg-rescue-bg focus:bg-white focus:border-rescue-ink focus:outline-none px-3 py-2 text-sm font-normal normal-case tracking-normal text-rescue-ink"
                />
              </label>
              <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-rescue-mute">
                Handicap / mobilité (détails)
                <input
                  data-testid="medical-disability"
                  value={form.disability || ""}
                  onChange={(e) => setForm({ ...form, disability: e.target.value })}
                  className="mt-1 w-full border-2 border-rescue-line bg-rescue-bg focus:bg-white focus:border-rescue-ink focus:outline-none px-3 py-2 text-sm font-normal normal-case tracking-normal text-rescue-ink"
                />
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 border-2 border-rescue-line px-3 py-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="medical-pregnancy"
                    checked={!!form.pregnancy}
                    onChange={(e) => setForm({ ...form, pregnancy: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span>Grossesse en cours</span>
                </label>
                <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-rescue-mute">
                  Langue préférée
                  <select
                    data-testid="medical-language"
                    value={form.preferred_language || ""}
                    onChange={(e) => setForm({ ...form, preferred_language: e.target.value })}
                    className="mt-1 w-full border-2 border-rescue-line bg-rescue-bg focus:bg-white focus:border-rescue-ink focus:outline-none px-3 py-2 text-sm font-normal normal-case tracking-normal text-rescue-ink"
                  >
                    <option value="">— Non renseigné —</option>
                    <option value="fr">Français</option>
                    <option value="en">English</option>
                  </select>
                </label>
              </div>

              <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-rescue-mute">
                Autres notes médicales
                <textarea
                  data-testid="medical-notes"
                  value={form.notes || ""}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  placeholder="Toute information supplémentaire utile aux secours…"
                  className="mt-1 w-full border-2 border-rescue-line bg-rescue-bg focus:bg-white focus:border-rescue-ink focus:outline-none px-3 py-2 text-sm font-normal normal-case tracking-normal text-rescue-ink"
                />
              </label>

              <div
                className="text-[11px] leading-relaxed text-rescue-mute border-l-4 border-mali-yellow bg-mali-yellow/5 px-3 py-2.5 flex gap-2"
                data-testid="medical-profile-privacy-consent"
              >
                <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-0.5 text-mali-yellow" />
                <span>
                  En envoyant une alerte SOS, vous acceptez que les informations de
                  votre profil d’urgence soient visibles par les équipes autorisées
                  qui traitent votre alerte.
                </span>
              </div>

              <div className="flex gap-2 pt-1">
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="medical-save-btn"
                  className="flex-1 bg-rescue-ink text-white py-3 text-xs font-bold uppercase tracking-[0.2em] flex items-center justify-center gap-2 disabled:opacity-60 hover:bg-rescue-red transition-colors"
                >
                  <Save className="w-4 h-4" /> {saving ? "Enregistrement…" : "Enregistrer"}
                </button>
                <button
                  type="button"
                  onClick={clear}
                  data-testid="medical-clear-btn"
                  className="px-4 py-3 text-xs font-bold uppercase tracking-[0.2em] border-2 border-rescue-line text-rescue-mute hover:border-rescue-red hover:text-rescue-red flex items-center gap-2 transition-colors"
                >
                  <Trash2 className="w-4 h-4" /> Effacer
                </button>
              </div>
            </>
          )}
        </form>
      )}
    </section>
  );
}

// ─── Wallet card (QR + printable A6 PDF) ───────────────────────────
// Iter75 — Renders an offline-readable QR of the profile PLUS a
// button that downloads a printable A6 PDF from
// `GET /api/citizen/medical-profile/pdf`. The citizen can fold the
// PDF into a wallet card; a responder scans the QR with any camera
// app and sees the medical essentials without needing internet.
function MedicalCardSection() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const phone = userIdentity.getPhone();
  const cfg = useCountryConfig();
  const brandUpper = (cfg?.brand_name || "SOS Mali").toUpperCase();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Sep 2026 bugfix — see identity.js `ready()` comment.
        await userIdentity.ready();
        const p = await getMedicalProfile();
        if (!cancelled) setProfile(p || {});
      } catch (_e) {
        if (!cancelled) setProfile({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const CONDITION_LABELS = {
    diabetes: "Diabète", hypertension: "Hypertension", heart: "Problèmes cardiaques",
    asthma: "Asthme", seizures: "Épilepsie / crises", stroke_history: "Antécédent d'AVC",
    sickle_cell: "Drépanocytose", severe_allergy: "Allergie sévère",
    pregnancy: "Grossesse", disability: "Handicap",
  };
  const qrText = React.useMemo(() => {
    if (!profile) return "";
    const lines = [`${brandUpper} - CARTE MEDICALE D'URGENCE`];
    if (profile.full_name) lines.push(`Nom: ${profile.full_name}`);
    if (profile.age) lines.push(`Age: ${profile.age}`);
    if (profile.blood_type) lines.push(`Groupe: ${profile.blood_type}`);
    const conds = (profile.conditions || []).map((k) => CONDITION_LABELS[k] || k);
    if (profile.other_condition) conds.push(profile.other_condition);
    if (conds.length) lines.push("Conditions: " + conds.join(", "));
    if (profile.allergies) lines.push(`Allergies: ${profile.allergies}`);
    if (profile.medications) lines.push(`Traitements: ${profile.medications}`);
    if (profile.disability) lines.push(`Handicap: ${profile.disability}`);
    if (phone) lines.push(`Tel: ${phone}`);
    return lines.join("\n");
  }, [profile, phone, brandUpper]);

  const download = async () => {
    if (!phone) { toast.error("Aucun numéro associé"); return; }
    setDownloading(true);
    try {
      const url = `${API}/citizen/medical-profile/pdf`;
      const res = await fetch(url, {
        credentials: "include",
        headers: userIdentity.getToken() ? { "X-Citizen-Token": userIdentity.getToken() } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = "carte-medicale-sosmali.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 500);
      toast.success("Carte médicale téléchargée");
    } catch (err) {
      toast.error("Téléchargement impossible");
    } finally {
      setDownloading(false);
    }
  };

  const hasAnything =
    profile && (
      profile.blood_type || profile.full_name ||
      (Array.isArray(profile.conditions) && profile.conditions.length > 0) ||
      profile.allergies || profile.medications
    );

  return (
    <section
      className="mx-5 mb-6 border-2 border-rescue-line bg-white"
      data-testid="medical-card-section"
    >
      <div className="flex items-center justify-between px-4 py-3.5 border-b-2 border-rescue-line bg-mali-navy text-white">
        <div className="flex items-center gap-2">
          <QrCode className="w-4 h-4" />
          <span className="text-[11px] font-bold uppercase tracking-[0.2em]">
            Carte médicale imprimable
          </span>
        </div>
      </div>

      <div className="p-4 space-y-3">
        <p className="text-[11px] leading-relaxed text-rescue-mute">
          Générez une carte au format A6 à plier dans votre portefeuille.
          Le QR code est lisible par n’importe quel appareil photo, même sans internet.
        </p>

        <div className="flex items-start gap-3">
          <div className="shrink-0 bg-white p-2 border-2 border-rescue-line rounded-md" data-testid="medical-qr-preview">
            {loading || !qrText ? (
              <div className="w-24 h-24 flex items-center justify-center text-[10px] text-rescue-mute">
                {loading ? "…" : "—"}
              </div>
            ) : (
              <QRCodeSVG value={qrText} size={96} level="M" includeMargin={false} />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-rescue-mute mb-1">
              Aperçu QR
            </div>
            <p className="text-[11px] text-rescue-mute mb-2">
              {hasAnything
                ? "Ce QR code contient vos informations médicales."
                : "Complétez votre profil médical ci-dessus pour enrichir la carte."}
            </p>
            <button
              type="button"
              onClick={download}
              disabled={downloading || !phone}
              data-testid="medical-download-pdf-btn"
              className="w-full bg-rescue-ink text-white py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] flex items-center justify-center gap-2 disabled:opacity-60 hover:bg-rescue-red transition-colors"
            >
              <Printer className="w-4 h-4" />
              {downloading ? "Préparation…" : "Télécharger la carte A6 (PDF)"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ProfilPage() {
  return (
    <div className="flex-1 flex flex-col" data-testid="profil-page">
      <header className="px-5 pt-5 pb-3">
        <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-rescue-mute">Profil</div>
        <h1 className="font-display text-3xl font-black tracking-tight leading-none mt-1">
          Mon profil d’urgence
        </h1>
        <p className="text-sm text-rescue-mute mt-2">
          Informations médicales optionnelles et contacts d’urgence. En cas d’alerte SOS,
          ces informations aident les équipes autorisées à intervenir plus rapidement.
        </p>
      </header>

      <MedicalProfileSection />

      <MedicalCardSection />

      <div className="px-5 pb-1">
        <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-rescue-mute mb-1">Contacts</div>
        <h2 className="font-display text-xl font-black tracking-tight leading-none mb-2">
          Contacts d’urgence
        </h2>
        <p className="text-[12px] text-rescue-mute mb-3">
          Ces personnes reçoivent automatiquement un SMS et un e-mail avec votre position lorsque vous envoyez une alerte.
        </p>
      </div>

      <EmergencyContactsSection />
    </div>
  );
}
